import { Injectable, Inject } from '@angular/core';
import { CommonService } from '@ecosure-suite/shared/utils-helpers';
import {
  ClaimLineForm,
  FuneralCoverClaimLine,
  PersonalAccidentCoverClaimLine,
  HospitalCashBackClaimLine,
  ClaimLine
} from '@ecosure-suite/shared/util-entities';
import {
  CLAIM_LINE_TYPES_CONFIGURATION,
  ClaimLineTypesConfiguration
} from '../tokens/claim-line-types.Configuration';

@Injectable({
  providedIn: 'root'
})
export class ClaimLinesConverterService {
  claimLineTypes: ClaimLineTypesConfiguration;

  constructor(
    @Inject(CLAIM_LINE_TYPES_CONFIGURATION)
    configuration: ClaimLineTypesConfiguration,
    private common: CommonService
  ) {
    this.claimLineTypes = configuration;
  }

  getRequestBody(claimLine: Partial<ClaimLineForm>): { [key: string]: any } {
    const requestBody = {
      claimLineId: claimLine.id,
      claimCauseId: claimLine.cause ? claimLine.cause.id : undefined,
      claimLineTypeId: claimLine.claimTypeId,
      claimId: claimLine.claimId,
      claimCauseDescription: claimLine.cause ? claimLine.cause.name : undefined
    };

    switch (claimLine.claimTypeId) {
      case this.claimLineTypes.funeral.id: {
        if (!claimLine.type) {
          return requestBody;
        }
        const funeralCoverClaimLine = <FuneralCoverClaimLine>claimLine.type;
        return Object.assign({}, requestBody, {
          dateOfDeath: funeralCoverClaimLine.dateOfDeath
            ? CommonService.formatDate(
                funeralCoverClaimLine.dateOfDeath,
                'DD-MM-YYYYTHH:mm:ss'
              )
            : undefined,
          placeOfDeath: funeralCoverClaimLine.placeOfDeath,
          whoIdentifiedTheDeceased: funeralCoverClaimLine.deceasedIdentifiedBy,
          policeStationReported: funeralCoverClaimLine.policeStationReported
        });
      }
      case this.claimLineTypes.personalAccidentCover.id: {
        if (!claimLine.type) {
          return requestBody;
        }
        const pacClaimLine = <PersonalAccidentCoverClaimLine>claimLine.type;
        return Object.assign({}, requestBody, {
          dateOfAccident: pacClaimLine.dateOfAccident
            ? CommonService.formatDate(
                pacClaimLine.dateOfAccident,
                'DD-MM-YYYYTHH:mm:ss'
              )
            : undefined,
          placeOfAccident: pacClaimLine.placeOfAccident,
          accidentReportedToPolice: pacClaimLine.accidentReportedToPolice,
          reasonForNotReportingToPolice:
            pacClaimLine.reasonForNotReportingToPolice,
          disabilityDescription: pacClaimLine.disabilityDescription,
          anyFormOfDisabilityBeforeAccident: pacClaimLine.hadPriorDisability,
          descriptionOfDisabilityBeforeAccident:
            pacClaimLine.priorDisabilityDescription
        });
      }
      case this.claimLineTypes.hospitalCashback.id: {
        if (!claimLine.type) {
          return requestBody;
        }
        const hcbClaimLine = <HospitalCashBackClaimLine>claimLine.type;
        return Object.assign({}, requestBody, {
          nameOfHospital: hcbClaimLine.hospital
            ? hcbClaimLine.hospital.name
            : undefined,
          dateOfHospitalisation: hcbClaimLine.dateOfHospitalisation
            ? CommonService.formatDate(
                hcbClaimLine.dateOfHospitalisation,
                'DD-MM-YYYYTHH:mm:ss'
              )
            : undefined,
          dateOfDischarge: hcbClaimLine.dateOfDischarge
            ? CommonService.formatDate(
                hcbClaimLine.dateOfDischarge,
                'DD-MM-YYYYTHH:mm:ss'
              )
            : undefined,
          addressOfHospital: hcbClaimLine.hospital
            ? hcbClaimLine.hospital.address
            : undefined,
          telephoneOfHospital: hcbClaimLine.hospital
            ? hcbClaimLine.hospital.telephone
            : undefined,
          nameOfAdmittingDoctor: hcbClaimLine.admittingDoctor
            ? hcbClaimLine.admittingDoctor.name
            : undefined,
          ahfoznumberOfAdmittingDoctor: hcbClaimLine.admittingDoctor
            ? hcbClaimLine.admittingDoctor.value
            : undefined,
          nameOfDischargingDoctor: hcbClaimLine.dischargingDoctor
            ? hcbClaimLine.dischargingDoctor.name
            : undefined,
          ahfoznumberOfDischargingDoctor: hcbClaimLine.dischargingDoctor
            ? hcbClaimLine.dischargingDoctor.value
            : undefined,
          medicalAidProvider: hcbClaimLine.medicalAid
            ? hcbClaimLine.medicalAid.name
            : undefined,
          medicalAidNumber: hcbClaimLine.medicalAid
            ? hcbClaimLine.medicalAid.value
            : undefined
        });
      }
      default: {
        throw new Error(`unknown claim type Id: '${claimLine.claimTypeId}'`);
      }
    }
  }

  convertClaimLine(data: any, claimRequestId: number = null): ClaimLine {
    const claimLine: ClaimLine = {
      id: data.claimLineId,
      claimRequestId: claimRequestId,
      claimTypeId: +data.claimLineTypeId,
      name: ``,
      documentOwnerType: data.documentOwnerType,
      claimId: data.claimId,
      cause: {
        id: data.claimCauseId,
        name: data.claimCauseDescription,
        typeId: data.claimCauseTypeId
      },
      creationDate: data.creationDate ? new Date(data.creationDate) : undefined,
      createdBy: data.createdBy,
      assignedTo: data.assignedTo,
      status: data.claimLineStatus
        ? data.claimLineStatus.replace(/_/g, ' ')
        : undefined,
      type: undefined
    };

    switch (+data.claimLineTypeId) {
      case this.claimLineTypes.funeral.id: {
        claimLine.name = this.claimLineTypes.funeral.name;
        claimLine.type = {
          dateOfDeath: data.dateOfDeath
            ? new Date(data.dateOfDeath)
            : undefined,
          placeOfDeath: data.placeOfDeath,
          deceasedIdentifiedBy: data.whoIdentifiedTheDeceased,
          policeStationReported: data.policeStationReported
        } as FuneralCoverClaimLine;
        return claimLine;
      }

      case this.claimLineTypes.hospitalCashback.id: {
        claimLine.name = this.claimLineTypes.hospitalCashback.name;
        claimLine.type = {
          hospital: {
            name: data.nameOfHospital,
            address: data.addressOfHospital,
            telephone: data.telephoneOfHospital
          },
          dateOfHospitalisation: data.dateOfHospitalisation
            ? new Date(data.dateOfHospitalisation)
            : undefined,
          dateOfDischarge: data.dateOfDischarge
            ? new Date(data.dateOfDischarge)
            : undefined,
          admittingDoctor: {
            name: data.nameOfAdmittingDoctor,
            value: data.ahfoznumberOfAdmittingDoctor
          },
          dischargingDoctor: {
            name: data.nameOfDischargingDoctor,
            value: data.ahfoznumberOfDischargingDoctor
          },
          medicalAid: {
            name: data.medicalAidProvider,
            value: data.medicalAidNumber
          }
        } as HospitalCashBackClaimLine;
        return claimLine;
      }

      case this.claimLineTypes.personalAccidentCover.id: {
        claimLine.name = this.claimLineTypes.personalAccidentCover.name;
        claimLine.type = {
          dateOfAccident: data.dateOfAccident
            ? new Date(data.dateOfAccident)
            : undefined,
          placeOfAccident: data.placeOfAccident,
          accidentReportedToPolice: data.accidentReportedToPolice,
          reasonForNotReportingToPolice: data.reasonForNotReportingToPolice,
          disabilityDescription: data.disabilityDescription,
          hadPriorDisability: data.anyFormOfDisabilityBeforeAccident,
          priorDisabilityDescription: data.descriptionOfDisabilityBeforeAccident
        } as PersonalAccidentCoverClaimLine;
        return claimLine;
      }

      default: {
        throw new Error(`Unknown claimLine type Id: '${data.claimLineTypeId}`);
      }
    }
  }
}
